export interface PlanBundleServiceLike {
  plan_id?: string;
  usage_limit?: number | null;
  limit_type?: string | null;
  inclusion_type?: string | null;
  quantity_label?: string | null;
  limit_label?: string | null;
  services?: {
    service_name?: string | null;
    service_key?: string | null;
  } | null;
}

interface PlanLike {
  id?: string;
  plan_key?: string | null;
  plan_name?: string | null;
  metadata_json?: unknown;
}

type Platform = 'forkhire' | 'forkseek';

const BASELINE_SERVICES: Record<Platform, Record<string, PlanBundleServiceLike[]>> = {
  forkhire: {
    free: [
      service('ai_cv_scoring', 'AI CV Scoring', 5),
      service('ai_interviews', 'AI Interview Scoring', 2),
      service('ai_job_generator', 'AI Job Generator', 3),
      service('talent_pool', 'Talent Pool Invitations', 2),
    ],
    starter: [
      service('ai_cv_scoring', 'AI CV Scoring', 100),
      service('ai_interviews', 'AI Interview Scoring', 25),
      service('ai_job_generator', 'AI Job Generator', 50),
      service('talent_pool', 'Talent Pool Invitations', 25),
    ],
    growth: [
      service('ai_cv_scoring', 'AI CV Scoring', 500),
      service('ai_interviews', 'AI Interview Scoring', 150),
      service('ai_job_generator', 'AI Job Generator', 200),
      service('talent_pool', 'Talent Pool Invitations', 150),
    ],
    enterprise: [
      service('ai_cv_scoring', 'AI CV Scoring', null, 'unlimited', 'bundled'),
      service('ai_interviews', 'AI Interview Scoring', null, 'unlimited', 'bundled'),
      service('ai_job_generator', 'AI Job Generator', null, 'unlimited', 'bundled'),
      service('talent_pool', 'Talent Pool Invitations', null, 'unlimited', 'bundled'),
    ],
  },
  forkseek: {
    free: [
      service('cv_review', 'Expert CV Review', 0),
      service('cv_builder', 'CV Builder', 1),
      service('interview_practice', 'Interview Practice', 3),
      service('job_discovery', 'Job Discovery', 10),
    ],
    plus: [
      service('cv_review', 'Expert CV Review', 3),
      service('cv_builder', 'CV Builder', 10),
      service('interview_practice', 'Interview Practice', 20),
      service('job_discovery', 'Job Discovery', 75),
    ],
    pro: [
      service('cv_review', 'Expert CV Review', 10),
      service('cv_builder', 'CV Builder', 30),
      service('interview_practice', 'Interview Practice', 75),
      service('job_discovery', 'Job Discovery', 200),
    ],
  },
};

function service(
  serviceKey: string,
  serviceName: string,
  usageLimit: number | null,
  limitType = 'monthly',
  inclusionType = 'quota',
): PlanBundleServiceLike {
  return {
    usage_limit: usageLimit,
    limit_type: limitType,
    inclusion_type: inclusionType,
    services: {
      service_key: serviceKey,
      service_name: serviceName,
    },
  };
}

function localizeMetadataText(value: unknown) {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    const localized = value as Record<string, unknown>;
    return String(localized.en || localized.ar || localized.fr || '').trim();
  }
  return '';
}

function normalizePlanIdentity(plan: PlanLike) {
  return `${plan.plan_key || ''} ${plan.plan_name || ''}`.toLowerCase();
}

function getBaselineKey(platform: Platform, plan: PlanLike) {
  const identity = normalizePlanIdentity(plan);

  if (identity.includes('free')) return 'free';
  if (platform === 'forkhire') {
    if (identity.includes('enterprise')) return 'enterprise';
    if (identity.includes('growth') || identity.includes('professional') || identity.includes('pro')) return 'growth';
    if (identity.includes('starter') || identity.includes('expert')) return 'starter';
  }
  if (identity.includes('professional') || identity.includes('pro')) return 'pro';
  if (identity.includes('expert') || identity.includes('plus')) return 'plus';

  return '';
}

function getMetadataBundleServices(plan: PlanLike): PlanBundleServiceLike[] {
  const metadata = plan.metadata_json as { public?: { limits?: unknown[] } } | null;
  const limits = metadata?.public?.limits;
  if (!Array.isArray(limits)) return [];

  return limits
    .map((limit, index) => {
      const row = limit as { label?: unknown; value?: unknown };
      const label = localizeMetadataText(row.label);
      const value = localizeMetadataText(row.value);
      if (!label || !value) return null;

      const quantityMatch = value.match(/\d+/);
      const usageLimit = quantityMatch ? Number(quantityMatch[0]) : null;
      const limitType = /year|annual/i.test(value) ? 'yearly' : /day|daily/i.test(value) ? 'daily' : /month|monthly/i.test(value) ? 'monthly' : 'total';

      return {
        plan_id: plan.id,
        usage_limit: Number.isFinite(usageLimit) ? usageLimit : null,
        limit_type: usageLimit == null ? 'unlimited' : limitType,
        inclusion_type: 'quota',
        quantity_label: usageLimit == null ? value : null,
        limit_label: usageLimit == null ? null : value.replace(String(usageLimit), '').trim(),
        services: {
          service_key: `metadata_${index}`,
          service_name: label,
        },
      };
    })
    .filter(Boolean) as PlanBundleServiceLike[];
}

export function getPlanBundleServices(
  plan: PlanLike,
  services: PlanBundleServiceLike[],
  platform: Platform,
) {
  if (services.length > 0) return services;

  const metadataServices = getMetadataBundleServices(plan);
  if (metadataServices.length > 0) return metadataServices;

  const baselineKey = getBaselineKey(platform, plan);
  const baselineServices = baselineKey ? BASELINE_SERVICES[platform][baselineKey] : [];

  return baselineServices.map((baselineService) => ({
    ...baselineService,
    plan_id: plan.id,
    services: baselineService.services ? { ...baselineService.services } : null,
  }));
}
